/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type {
  MergeState,
  MultiPropertyResolution,
  SinglePropertyResolution,
} from './types';

interface MergeWizardContextValue extends MergeState {
  setCanonicalId(id: string | null): void;
  setSourceId(id: string | null): void;
  setSinglePropertyResolution(
    propertyName: string,
    resolution: SinglePropertyResolution
  ): void;
  setMultiPropertyResolution(
    propertyName: string,
    resolution: MultiPropertyResolution
  ): void;
  addDismissedAssociation(associationId: string): void;
  removeDismissedAssociation(associationId: string): void;
  setAssociationChoice(associationTypeName: string, targetId: string): void;
  reset(): void;
  clearPropertyResolutions(): void;
}

const MergeWizardContext = createContext<MergeWizardContextValue | null>(null);

export function useMergeWizard(): MergeWizardContextValue {
  const context = useContext(MergeWizardContext);
  if (!context) {
    throw new Error('useMergeWizard must be used within a MergeWizardProvider');
  }
  return context;
}

export function MergeWizardProvider({ children }: { children: ReactNode }) {
  const [canonicalId, setCanonicalId] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [singlePropertyResolutions, setSinglePropertyResolutions] = useState<
    Record<string, SinglePropertyResolution>
  >({});
  const [multiPropertyResolutions, setMultiPropertyResolutions] = useState<
    Record<string, MultiPropertyResolution>
  >({});
  const [dismissedAssociationIds, setDismissedAssociationIds] = useState<
    Set<string>
  >(new Set());
  const [associationChoices, setAssociationChoices] = useState<
    Record<string, string>
  >({});

  const setSinglePropertyResolution = useCallback(
    (propertyName: string, resolution: SinglePropertyResolution) => {
      setSinglePropertyResolutions(prev => ({
        ...prev,
        [propertyName]: resolution,
      }));
    },
    []
  );

  const setMultiPropertyResolution = useCallback(
    (propertyName: string, resolution: MultiPropertyResolution) => {
      setMultiPropertyResolutions(prev => ({
        ...prev,
        [propertyName]: resolution,
      }));
    },
    []
  );

  const addDismissedAssociation = useCallback((associationId: string) => {
    setDismissedAssociationIds(prev => new Set([...prev, associationId]));
  }, []);

  const removeDismissedAssociation = useCallback((associationId: string) => {
    setDismissedAssociationIds(prev => {
      const next = new Set(prev);
      next.delete(associationId);
      return next;
    });
  }, []);

  const setAssociationChoice = useCallback(
    (associationTypeName: string, targetId: string) => {
      setAssociationChoices(prev => ({
        ...prev,
        [associationTypeName]: targetId,
      }));
    },
    []
  );

  const reset = useCallback(() => {
    setCanonicalId(null);
    setSourceId(null);
    setSinglePropertyResolutions({});
    setMultiPropertyResolutions({});
    setDismissedAssociationIds(new Set());
    setAssociationChoices({});
  }, []);

  const clearPropertyResolutions = useCallback(() => {
    setSinglePropertyResolutions({});
    setMultiPropertyResolutions({});
    setDismissedAssociationIds(new Set());
    setAssociationChoices({});
  }, []);

  const value = useMemo<MergeWizardContextValue>(
    () => ({
      canonicalId,
      sourceId,
      singlePropertyResolutions,
      multiPropertyResolutions,
      dismissedAssociationIds,
      associationChoices,
      setCanonicalId,
      setSourceId,
      setSinglePropertyResolution,
      setMultiPropertyResolution,
      addDismissedAssociation,
      removeDismissedAssociation,
      setAssociationChoice,
      reset,
      clearPropertyResolutions,
    }),
    [
      canonicalId,
      sourceId,
      singlePropertyResolutions,
      multiPropertyResolutions,
      dismissedAssociationIds,
      associationChoices,
      setSinglePropertyResolution,
      setMultiPropertyResolution,
      addDismissedAssociation,
      removeDismissedAssociation,
      setAssociationChoice,
      reset,
      clearPropertyResolutions,
    ]
  );

  return (
    <MergeWizardContext.Provider value={value}>
      {children}
    </MergeWizardContext.Provider>
  );
}
